import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Edit3, Trash2, Search, Tag } from "lucide-react";
import PaymentCategoryFormModal from "./PaymentCategoryFormModal";
import paymentService from "@/services/paymentService";
import { showError, showInfo } from "@/store/errorStore";

/**
 * Componente de gerenciamento de categorias de pagamento
 * CRUD completo para categorias de pagamento
 */
export default function PaymentCategoriesManagement() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [savingCategory, setSavingCategory] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  /**
   * Carregar categorias
   */
  const loadData = async () => {
    setLoading(true);
    try {
      const data = await paymentService.getAllCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(
        error.response?.data?.message || "Erro ao carregar categorias",
        "Erro de Carregamento"
      );
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Abrir modal para criar nova categoria
   */
  const handleCreate = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  /**
   * Abrir modal para editar categoria
   */
  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  /**
   * Salvar categoria (criar ou atualizar)
   */
  const handleSave = async (categoryData) => {
    setSavingCategory(true);
    try {
      if (editingCategory) {
        await paymentService.updateCategory(editingCategory.id, categoryData);
        showInfo("Categoria atualizada com sucesso!", "Sucesso");
      } else {
        await paymentService.createCategory(categoryData);
        showInfo("Categoria criada com sucesso!", "Sucesso");
      }
      await loadData();
      setShowModal(false);
      setEditingCategory(null);
    } catch (error) {
      showError(
        error.response?.data?.message ||
          error.message ||
          "Erro ao salvar categoria",
        "Erro"
      );
    } finally {
      setSavingCategory(false);
    }
  };

  /**
   * Abrir diálogo de confirmação para deletar
   */
  const handleDeleteClick = (category) => {
    setDeletingCategoryId(category.id);
    setShowDeleteDialog(true);
  };

  /**
   * Confirmar exclusão da categoria
   */
  const handleConfirmDelete = async () => {
    if (!deletingCategoryId) return;

    try {
      await paymentService.deleteCategory(deletingCategoryId);
      showInfo("Categoria excluída com sucesso!", "Sucesso");
      await loadData();
    } catch (error) {
      showError(
        error.response?.data?.message || "Erro ao excluir categoria",
        "Erro"
      );
    } finally {
      setShowDeleteDialog(false);
      setDeletingCategoryId(null);
    }
  };

  /**
   * Filtrar categorias por busca
   */
  const filteredCategories = categories.filter((category) => {
    const query = searchQuery.toLowerCase();
    const name = (category.name || "").toLowerCase();
    const description = (category.description || "").toLowerCase();

    return name.includes(query) || description.includes(query);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Categorias de Pagamento
          </h1>
          <p className="text-gray-500 mt-1">
            {filteredCategories.length}{" "}
            {filteredCategories.length === 1 ? "categoria" : "categorias"}{" "}
            encontradas
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-[#c9332C] hover:bg-[#a02820]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Categoria
        </Button>
      </div>

      {/* Busca */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Buscar por nome ou descrição..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Categorias */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Categorias</CardTitle>
          <CardDescription>
            Gerencie as categorias de pagamento do sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando categorias...</p>
              </div>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Tag className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">
                {searchQuery
                  ? "Nenhuma categoria encontrada"
                  : "Nenhuma categoria cadastrada"}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {searchQuery
                  ? "Tente outro termo de busca"
                  : "Clique em 'Nova Categoria' para começar"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data de Criação</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCategories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell className="font-medium">
                        #{category.id}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {category.name}
                      </TableCell>
                      <TableCell className="text-gray-600 max-w-md truncate">
                        {category.description || "-"}
                      </TableCell>
                      <TableCell>
                        {category.active !== false ? (
                          <Badge
                            variant="outline"
                            className="bg-green-100 text-green-800 border-green-200"
                          >
                            Ativo
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="bg-gray-100 text-gray-800 border-gray-200"
                          >
                            Inativo
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {paymentService.formatDate(
                          category.createdAt || category.created_at
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(category)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(category)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Formulário */}
      <PaymentCategoryFormModal
        open={showModal}
        onOpenChange={setShowModal}
        category={editingCategory}
        onSave={handleSave}
        loading={savingCategory}
      />

      {/* Diálogo de Confirmação de Exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta categoria? Esta ação não pode
              ser desfeita e pode afetar registros vinculados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingCategoryId(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
